/**
 * User Role Assignment API.
 *
 * Uniqueness of (userObjectId, roleValue) is enforced at the app level —
 * there is no Dataverse alternate key yet. The grant path checks for an
 * existing row before creating. The revoke path takes an id.
 */

import { dataverseClient, TABLES } from '@shared/api';
import type {
  CreateUserRoleAssignmentInput,
  UserRoleAssignment,
} from '../model/types';

interface UserRoleAssignmentEntity {
  vba_userroleassignmentid: string;
  vba_name?: string;
  vba_userobjectid?: string;
  vba_userprincipalname?: string;
  vba_rolevalue?: string;
  vba_grantedby?: string;
  vba_grantedbyname?: string;
  vba_grantedon?: string;
  vba_notes?: string;
  [k: string]: unknown;
}

function mapToDomain(entity: UserRoleAssignmentEntity): UserRoleAssignment {
  return {
    id: entity.vba_userroleassignmentid,
    userObjectId: entity.vba_userobjectid ?? '',
    userPrincipalName: entity.vba_userprincipalname,
    roleValue: entity.vba_rolevalue ?? '',
    grantedBy: entity.vba_grantedby,
    grantedByName: entity.vba_grantedbyname,
    grantedOn: entity.vba_grantedon,
    notes: entity.vba_notes,
  };
}

function escape(s: string): string {
  return s.replace(/'/g, "''");
}

export const userRoleAssignmentApi = {
  /** List all assignments, or all for a specific user if userObjectId is provided. */
  async list(userObjectId?: string): Promise<UserRoleAssignment[]> {
    const filter = userObjectId
      ? `vba_userobjectid eq '${escape(userObjectId)}'`
      : undefined;
    const response = await dataverseClient.list<UserRoleAssignmentEntity>(
      TABLES.USER_ROLE_ASSIGNMENT,
      {
        ...(filter && { filter }),
        orderBy: 'vba_rolevalue asc',
      },
    );
    return response.value.map(mapToDomain);
  },

  async findByUserAndRole(
    userObjectId: string,
    roleValue: string,
  ): Promise<UserRoleAssignment | null> {
    const filter = [
      `vba_userobjectid eq '${escape(userObjectId)}'`,
      `vba_rolevalue eq '${escape(roleValue)}'`,
    ].join(' and ');
    const response = await dataverseClient.list<UserRoleAssignmentEntity>(
      TABLES.USER_ROLE_ASSIGNMENT,
      { filter, top: 1 },
    );
    if (response.value.length === 0) return null;
    return mapToDomain(response.value[0]!);
  },

  /**
   * Find a pending (pre-provisioned, not yet reconciled) assignment by email.
   * Pending rows have vba_userobjectid empty/null and vba_userprincipalname set.
   */
  async findPendingByEmailAndRole(
    email: string,
    roleValue: string,
  ): Promise<UserRoleAssignment | null> {
    const filter = [
      `vba_userprincipalname eq '${escape(email)}'`,
      `vba_rolevalue eq '${escape(roleValue)}'`,
      `vba_userobjectid eq null`,
    ].join(' and ');
    const response = await dataverseClient.list<UserRoleAssignmentEntity>(
      TABLES.USER_ROLE_ASSIGNMENT,
      { filter, top: 1 },
    );
    if (response.value.length === 0) return null;
    return mapToDomain(response.value[0]!);
  },

  /** All pending assignments for a given email — used by the BFF to reconcile on first sign-in. */
  async listPendingByEmail(email: string): Promise<UserRoleAssignment[]> {
    const filter = [
      `vba_userprincipalname eq '${escape(email)}'`,
      `vba_userobjectid eq null`,
    ].join(' and ');
    const response = await dataverseClient.list<UserRoleAssignmentEntity>(
      TABLES.USER_ROLE_ASSIGNMENT,
      { filter, orderBy: 'vba_rolevalue asc' },
    );
    return response.value.map(mapToDomain);
  },

  /** Patch the vba_userobjectid on a pending row once the user signs in. */
  async setUserObjectId(id: string, userObjectId: string): Promise<void> {
    await dataverseClient.update(TABLES.USER_ROLE_ASSIGNMENT, id, {
      vba_userobjectid: userObjectId,
    });
  },

  async grant(input: CreateUserRoleAssignmentInput): Promise<UserRoleAssignment> {
    // Find-then-create — avoids duplicates without a DB alternate key.
    // Pending grants (no oid yet) are deduped by (email, role) instead.
    const existing = input.userObjectId
      ? await this.findByUserAndRole(input.userObjectId, input.roleValue)
      : await this.findPendingByEmailAndRole(input.userPrincipalName, input.roleValue);
    if (existing) return existing;

    const entity: Record<string, unknown> = {
      vba_name: `${input.userPrincipalName}:${input.roleValue}`,
      // Omit oid for pending rows so Dataverse stores null (cleaner filter
      // semantics than empty string).
      ...(input.userObjectId && { vba_userobjectid: input.userObjectId }),
      vba_userprincipalname: input.userPrincipalName,
      vba_rolevalue: input.roleValue,
      vba_grantedby: input.grantedBy,
      vba_grantedbyname: input.grantedByName,
      vba_grantedon: new Date().toISOString(),
      ...(input.notes && { vba_notes: input.notes }),
    };
    const response = await dataverseClient.create<UserRoleAssignmentEntity>(
      TABLES.USER_ROLE_ASSIGNMENT,
      entity,
    );
    return mapToDomain(response);
  },

  async revoke(id: string): Promise<void> {
    await dataverseClient.remove(TABLES.USER_ROLE_ASSIGNMENT, id);
  },

  /** Revoke by (user, role) — convenience for the admin UI "uncheck" action. */
  async revokeByUserAndRole(userObjectId: string, roleValue: string): Promise<boolean> {
    const existing = await this.findByUserAndRole(userObjectId, roleValue);
    if (!existing) return false;
    await this.revoke(existing.id);
    return true;
  },

  /** Revoke a pending assignment by (email, role). */
  async revokePendingByEmailAndRole(email: string, roleValue: string): Promise<boolean> {
    const existing = await this.findPendingByEmailAndRole(email, roleValue);
    if (!existing) return false;
    await this.revoke(existing.id);
    return true;
  },
};
